<!--
  Component to render <search-box> element
  - Accepts the following properties:
  [id, type, name, placeholder, value, label, list]

  - Accepts optional callback property:
  [on-search-box-change, on-search-box-item-selected]

  @example

    <search-box
      ref="searchinput1"
      :id="my-123"
      :label="Search locations"
      :list='[{"id":9,"name":"Hello world","city":"San Luis"},{"id":10,"name":"Another test","city":"Santa Clara CA"}]'
      @on-search-box-item-selected="onSearchBoxItemSelected"
    />

  -->

<template lang="html">
  <section class="m-search-box">
    <div
      class="m-search-box"
      :id="id"
      v-clickoutside="onClickedOutside"
      @keydown.esc.stop="onEsc"
    >
      <div class="m-search-box">
        <label v-if="label" class="m-text-input__label">{{ label }}</label>

        <multi-selection-items
          v-if="isMultiSelect"
          :is-open="showResultsList"
          :list-items="initialSelectedItems"
          :is-read-only="isReadOnly"
          :has-icon="hasIcon"
          @on-up-arrow-pressed="onUpArrowPressed"
          @on-down-arrow-pressed="onDownArrowPressed"
          @on-tab-pressed="closeDropdown"
          @on-multi-select-focus="onInputFocus"
          @on-multi-select-blur="onInputBlur"
          @on-multi-item-selected="onSingleSelectPressEnter"
          @on-multi-select-close-options="onMultiSelectCloseOptions"
          @on-multi-select-remove-item="onMultSelectRemoveItem"
        />

        <!-- single selection --->
        <div class="m-selection" v-else-if="displaySelectedSingleItem">
          <div class="m-item-selected">
            <div class="m-selection-label">
              <span
                class="slds-icon_container slds-m-left_xx-small slds-icon-utility-text"
                v-if="hasIcon"
              >
                <svg class="slds-icon slds-icon_xx-small icon-margin">
                  <use :xlink:href="getUtilityIcon(selectedItem)"></use>
                </svg>
              </span>
              <div
                class="m-content-selection m-content"
                v-if="boxType === 'createApp' && selectedItem.Name"
              >
                {{ selectedItem.Formatted_Name_vod__c || selectedItem.Name }}
              </div>
              <div
                class="m-content-selection m-content"
                v-if="
                  !selectedItem.Name &&
                    boxType === 'createApp' &&
                    (selectedItem.Formatted_Name_vod__c ||
                      selectedItem.Account_vod__r.Formatted_Name_vod__c)
                "
              >
                {{
                  selectedItem.Formatted_Name_vod__c ||
                    selectedItem.Account_vod__r.Formatted_Name_vod__c
                }}
              </div>
              <div
                class="m-content-selection m-content"
                v-if="boxType === 'deEditModal' && inputType !== 'clause'"
              >
                {{ selectedItem.label }}
              </div>
              <div
                class="m-content-selection m-content"
                v-if="boxType === 'deEditModal' && inputType === 'clause'"
              >
                <span
                  v-if="
                    selectedItem.type === expressionType.STATIC &&
                      selectedItem.dataType === expressionType.STRING
                  "
                >
                  &quot;{{ selectedItem.value }}&quot;
                </span>
                <span
                  v-else-if="
                    selectedItem.type === expressionType.STATIC &&
                      selectedItem.dataType === expressionType.BOOLEAN
                  "
                >
                  {{ selectedItem.label }}
                </span>
                <span v-else>{{ selectedItem.value }}</span>
              </div>
            </div>
            <svg
              class="m-close-selection-icon"
              tabindex="0"
              v-if="!isReadOnly"
              @keydown.enter.stop="closeSelected"
              @click="closeSelected"
            >
              <use
                xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#close"
              ></use>
            </svg>
          </div>
        </div>
        <!--  // end single selection  -->

        <div v-if="isInputReady" class="m-search-container">
          <text-input
            :class="{ 'm-invalid': isInvalidInput }"
            v-model="currentTextValue"
            :type="type"
            :placeholder="placeholderText"
            :select="select"
            :focus="focus"
            :disabled="true"
            :is-multi-select="isMultiSelect"
            :has-right-icon="true"
            @on-up-arrow-pressed="onUpArrowPressed"
            @on-down-arrow-pressed="onDownArrowPressed"
            @on-tab-pressed="closeDropdown"
            @on-text-input-focus="onInputFocus"
            @on-text-input-blur="onInputBlur"
            @on-text-input-change="onInputChanged"
            @on-multi-select-press-enter="onMultiSelectPressEnter"
            @on-single-select-press-enter="onSingleSelectPressEnter"
            @click="onInputFocus"
          />
          <svg class="m-input-search-icon">
            <use
              xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#search"
            ></use>
          </svg>
        </div>
      </div>
      <div ref="resultList" class="m-result-list" v-if="showResultsList">
        <ul>
          <li
            class="m-item"
            :class="getClassForItem(item)"
            v-for="(item, index) in updateList"
            :key="item.Id"
          >
            <search-box-list-item
              class="m-list-option-text"
              :item-id="`${id}-${index}`"
              :box-type="boxType"
              :label-only="labelOnly"
              :has-des="hasDes"
              :list-item="item"
              :exp="exp"
              :has-icon="hasIcon"
              :is-multi-select="isMultiSelect"
              :is-selected="isSelected(item)"
              @on-search-box-item-selected="onSearchBoxItemSelected"
              @on-search-box-item-deselected="onMultSelectRemoveItem"
            />
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import debounce from "lodash.debounce";
  import TextInput from "@/components/TextInput";
  import SearchBoxListItem from "@/components/SearchBoxListItem";
  import MultiSelectionItems from "@/components/MultiSelectionItems";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import {mapState} from "vuex";

  export default  {
    name: 'search-box',
    components: {
      TextInput,
      SearchBoxListItem,
      MultiSelectionItems
    },
    props: {
      /**
       * input properties
       * @values id, type, name, placeholder, value, label, list
       */
      id: {
        type: String
      },
      type: {
        type: String,
        default: "text"
      },
      name: {
        type: String
      },
      placeholder: {
        type: String
      },
      value: {
        type: Object
      },
      values: {
        type: Array
      },
      multiSelect: {
        type: Boolean,
        default: false
      },
      textValue: {
        type: String
      },
      label: {
        type: String
      },
      list: {
        type: Array
      },
      boxType: {
        type: String
      },
      hasIcon: {
        type: Boolean
      },
      labelOnly: {
        type: Boolean
      },
      hasDes: {
        type: Boolean
      },
      inputType: {
        type: String
      },
      isInvalid: {
        type: Boolean,
        value: false
      },
      isReadOnly: {
        type: Boolean,
        default: false
      },
      isFirstInput: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        updateList: [],
        selectedItem: null,
        selectedListItems: [],
        initialSelectedItems: [],
        exp: "",
        showResultsList: false,
        isInvalidInput: false,
        currentTextValue: null,
        select: false,
        focus: false,
        selectedId: -1,
        isInputInFocus: false,
        isFocusReady: false
      }
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      isMultiSelect() {
        return this.multiSelect;
      },
      displaySelectedSingleItem() {
        return this.selectedItem && !this.selectedItem.isEmpty;
      },
      displaySingleSelection() {
        return !this.isMultiSelect && (!this.selectedItem || this.selectedItem.isEmpty);
      },
      displayForMultiSelect() {
        return this.isMultiSelect && (this.isInputInFocus || this.showResultsList);
      },
      isInputReady() {
        return (this.displayForMultiSelect || this.displaySingleSelection) && this.isFocusReady;
      },
      selectedSet() {
        return new Set(this.selectedListItems.map(e => e.name));
      },
      placeholderText() {
        if (typeof this.placeholder === 'string' || this.placeholder instanceof String) {
          return this.placeholder;
        } else {
          return this.getMessageText(this.messages.SEARCH);
        }
      }
    },
    watch: {
      textValue: function() {
        this.currentTextValue = "";
        this.$nextTick(() => {
          this.currentTextValue = this.textValue;
        });
      },
      value: {
        handler() {
          this.selectedItem = this.value;
        },
        immediate: true
      },
      values: {
        handler() {
          this.initialSelectedItems = this.values? [...this.values] : [];
          this.selectedListItems = this.values? [...this.values] : [];
        },
        immediate: true
      },
      list: {
        handler() {
          this.setList();
        },
        immediate: true,
        deep: true
      },
      isInvalid: function() {
        this.setInvalidValue();
      }
    },
    mounted() {
      if (this.isMultiSelect || this.isFirstInput) {
        this.focus = true;
      }
      this.isFocusReady = true;
      this.setList();
    },
    methods: {
      getClassForItem(item) {
        if (this.selectedId < 0) {
          return "";
        }

        let result;
        const visibleList = this.updateList.filter(e => e.visible);
        if (item === visibleList[this.selectedId] && this.isSelected(visibleList[this.selectedId])) {
          result = "selected-item-on-hover";
        } else if (item === visibleList[this.selectedId]) {
          result = 'item-on-hover-highlight';
        } else {
          result = ""
        }

        return result;
      },
      selectAnItem(id) {
        if (this.$refs.resultList) {
          this.selectedId = id;
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
            (c) => c.children.length > 0);
          if (filteredList[id]) {
            filteredList[id].scrollIntoView(false);
          }
        }
      },
      onUpArrowPressed() {
        if (this.selectedId > 0) {
          this.selectAnItem(this.selectedId - 1);
        }
      },
      onDownArrowPressed() {
        const visibleList = this.updateList.filter(e => e.visible);
        if (this.selectedId < visibleList.length - 1) {
          this.selectAnItem(this.selectedId + 1);
        }
      },
      onEsc() {
        this.closeDropdown();
      },
      onClickedOutside() {
        if (!this.isInputInFocus) {
          this.closeDropdown();
        }
      },
      closeDropdown() {
        this.showResultsList = false;
        this.isInputInFocus = false;
      },
      setList() {
        this.updateList = [];
        if(this.list) {
          for(const el of this.list) {
            const value = {...el};
            this.updateList.push(value);
          }
        }
      },
      setInvalidValue() {
        this.isInvalidInput = this.isInvalid;
      },
      closeSelected() {
        this.selectedItem = this.id ? {searchBoxId: this.id, isEmpty: true} : null;
        if(!this.isMultiSelect) {
          this.exp = "";
        }
        this.$emit("on-search-box-item-selected", this.selectedItem);
      },
      onSearchBoxItemSelected(item) {
        if (this.id) {
          item.searchBoxId = this.id;
        }
        if(this.isMultiSelect) {
          this.setMultiSelectItem(item);
        } else {
          this.setSingleItem(item);
        }
      },
      onInputFocus() {
        this.isInputInFocus = true;
        if (!this.isReadOnly) {
          this.showResultsList = true;
          this.isInvalidInput = false;
        }
      },
      onInputBlur() {
        this.isInputInFocus = false;
        if (!this.isMultiSelect) {
          setTimeout(() => {
            this.setInvalidValue(); // we don't know if it is valid or not, so let the user to validate again

            if(this.$listeners && this.$listeners["on-search-box-blur"]) {
              this.$emit("on-search-box-blur", this.exp, this.id || null);
            }
          }, 200);
        }
      },
      onInputChanged: debounce(function(val) {
        this.selectedId = -1;
        /**
         * Triggers when model value changes
         * @event on-search-box-change
         */
        if(this.$listeners && this.$listeners["on-search-box-change"]) {
          this.$emit("on-search-box-change", val, this.id || null);
        }
        this.exp = val;
      }, 400),
      setSingleItem(item) {
        this.selectedItem = item;
        this.$emit("on-search-box-item-selected", this.selectedItem);
        this.closeDropdown();
      },
      isSelected(item) {
        return this.selectedSet.has(item.name);
      },
      onMultiSelectCloseOptions() {
        this.showResultsList = false;
      },
      onMultSelectRemoveItem(item) {
        const filterd = this.selectedListItems.filter(e => e.name !== item.name);
        this.$emit("on-search-box-item-selected", filterd, this.id);
      },
      setMultiSelectItem(item) {
        this.selectedListItems.push(item);
        this.$emit("on-search-box-item-selected", this.selectedListItems, this.id);
        this.setHightlight();
      },
      setHightlight() {
        this.select = true;
        this.$nextTick(() => {
          // reset to trigger select next time
          this.select = false;
        });
      },
      onMultiSelectPressEnter() {
        const visibleList = this.updateList.filter(e => e.visible);
        const item = visibleList[this.selectedId];
        if(item) {
          if(this.isSelected(item)) {
            this.onMultSelectRemoveItem(item);
          } else {
            this.setMultiSelectItem(item);
          }
          this.setHightlight();
        }
      },
      getUtilityIcon(item) {
        return iconsUri.getUtilityIconByType(item.type);
      },
      onSingleSelectPressEnter() {
        if (this.selectedId >= 0) {
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[this.selectedId] && filteredList[this.selectedId].children[0]) {
            filteredList[this.selectedId].children[0].click();
          }
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .m-icon-selected {
    padding-left: 4px;
  }
  .m-selection-label {
    display: inline-flex;
    overflow: hidden;
    align-items: center;
    height: 24px;
  }
  .m-content {
    display: inline;
  }
  .selected-item-on-hover {
    background-color: #ff6a6a;
  }
  .item-on-hover-highlight {
    background-color: var(--HEADER_GRAY);
  }
</style>
